import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

import styles from './OrganizationDropdown.module.css';

const MIN_PANEL_WIDTH = 56;
const GAP_X = 8;

const computeCoords = (anchorEl, measuredWidth, measuredHeight, openUpward) => {
  const rect = anchorEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const maxAllowedWidth = Math.max(MIN_PANEL_WIDTH, viewportWidth - 16);
  const natural = typeof measuredWidth === 'number' && measuredWidth > 0 ? measuredWidth : MIN_PANEL_WIDTH;
  const width = Math.min(Math.max(natural, MIN_PANEL_WIDTH), maxAllowedWidth);

  // Prefer opening immediately to the right of the parent row (after the chevron).
  let left = rect.right + GAP_X;
  if (left + width > viewportWidth - 8) {
    // Flip to the left of the row when there is not enough room on the right.
    left = Math.max(8, rect.left - width - GAP_X);
  }

  let top = rect.top;
  if (openUpward && measuredHeight > 0) {
    top = Math.max(8, rect.bottom - measuredHeight);
  } else if (measuredHeight > 0 && top + measuredHeight > viewportHeight - 8) {
    top = Math.max(8, viewportHeight - measuredHeight - 8);
  }

  return { top, left };
};

export const SubDropdown = ({ children, className, onMouseEnter, onMouseLeave, anchorRef }) => {
  const [coords, setCoords] = useState(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchorRef?.current || typeof window === 'undefined') return;

    const update = () => {
      if (!anchorRef?.current) return;
      const el = panelRef.current;
      let measuredWidth = 0;
      let measuredHeight = 0;
      if (el) {
        const { width, height } = el.getBoundingClientRect();
        measuredWidth = width;
        measuredHeight = height;
      }
      const openUpward = Boolean(anchorRef.current.closest('[data-report-org-filter]'));
      setCoords(computeCoords(anchorRef.current, measuredWidth, measuredHeight, openUpward));
    };

    update();
    // Second pass: first layout may not include final font/checkbox metrics.
    const raf = window.requestAnimationFrame(() => update());
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, children]);

  const style = useMemo(
    () =>
      coords
        ? {
            top: coords.top,
            left: coords.left,
          }
        : { visibility: 'hidden' },
    [coords],
  );

  if (typeof document === 'undefined' || !anchorRef?.current) {
    return null;
  }

  
  return createPortal(
    <div
      ref={panelRef}
      className={[styles.subDropdown, className].filter(Boolean).join(' ')}
      data-org-filter-sub-panel
      role='presentation'
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={style}>
      {children}
    </div>,
    document.body,
  );
};

SubDropdown.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  anchorRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
};
